import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Save, FileDown, FileUp } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { toast } from "@/hooks/use-toast";

const BLOCK_TYPES = [
  { value: "warmup", labelKey: "training.blockType.warmup" },
  { value: "drill", labelKey: "training.blockType.drill" },
  { value: "match_play", labelKey: "training.blockType.matchPlay" },
  { value: "tactics", labelKey: "training.blockType.tactics" },
  { value: "cooldown", labelKey: "training.blockType.cooldown" },
  { value: "other", labelKey: "training.blockType.other" },
];

const FOCUS_AREAS = [
  { value: "serve", labelKey: "training.focusArea.serve" },
  { value: "volley", labelKey: "training.focusArea.volley" },
  { value: "return", labelKey: "training.focusArea.return" },
  { value: "bandeja", labelKey: "training.focusArea.bandeja" },
  { value: "smash", labelKey: "training.focusArea.smash" },
  { value: "wall_play", labelKey: "training.focusArea.wallPlay" },
  { value: "positioning", labelKey: "training.focusArea.positioning" },
  { value: "footwork", labelKey: "training.focusArea.footwork" },
];

interface Block {
  block_type: string;
  title: string;
  description: string;
  duration_minutes: number | null;
  exercise_id: string | null;
}

interface Props {
  sessionId: string;
  onBack: () => void;
}

export default function SessionPlanner({ sessionId, onBack }: Props) {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [focusAreas, setFocusAreas] = useState<string[]>([]);
  const [summary, setSummary] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [showTemplateSave, setShowTemplateSave] = useState(false);
  const [showTemplateLoad, setShowTemplateLoad] = useState(false);

  const { data: session } = useQuery({
    queryKey: ["session-detail", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("training_sessions")
        .select("*, training_groups(name, color), profiles!training_sessions_coach_id_fkey(full_name), courts(name)")
        .eq("id", sessionId)
        .single();
      return data;
    },
  });

  const { data: existingBlocks = [] } = useQuery({
    queryKey: ["session-blocks", sessionId],
    queryFn: async () => {
      const { data } = await supabase
        .from("session_blocks")
        .select("*")
        .eq("session_id", sessionId)
        .order("sort_order");
      return data ?? [];
    },
  });

  const { data: exercises = [] } = useQuery({
    queryKey: ["exercises-for-planner", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("exercises")
        .select("id, name")
        .eq("organization_id", organization!.id)
        .order("name");
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["session-templates", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("session_templates")
        .select("*")
        .eq("organization_id", organization!.id)
        .order("name");
      return data ?? [];
    },
    enabled: !!organization?.id,
  });

  // Load existing blocks into state
  useEffect(() => {
    if (existingBlocks.length > 0 && blocks.length === 0) {
      setBlocks(
        existingBlocks.map((b) => ({
          block_type: b.block_type,
          title: b.title || "",
          description: b.description || "",
          duration_minutes: b.duration_minutes,
          exercise_id: b.exercise_id,
        }))
      );
    }
    if (session) {
      setFocusAreas(session.focus_areas || []);
      setSummary(session.summary || "");
    }
  }, [existingBlocks, session]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Save blocks
      await supabase.from("session_blocks").delete().eq("session_id", sessionId);
      if (blocks.length > 0) {
        const rows = blocks.map((b, i) => ({
          session_id: sessionId,
          block_type: b.block_type,
          title: b.title || null,
          description: b.description || null,
          duration_minutes: b.duration_minutes || null,
          exercise_id: b.exercise_id || null,
          sort_order: i,
        }));
        const { error } = await supabase.from("session_blocks").insert(rows);
        if (error) throw error;
      }
      // Update session focus_areas and summary
      const { error: sesError } = await supabase
        .from("training_sessions")
        .update({ focus_areas: focusAreas, summary: summary || null })
        .eq("id", sessionId);
      if (sesError) throw sesError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session-blocks", sessionId] });
      queryClient.invalidateQueries({ queryKey: ["admin-sessions"] });
      toast({ title: t("training.planSaved") });
    },
    onError: () => toast({ title: t("training.saveFailed"), variant: "destructive" }),
  });

  const saveTemplateMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("session_templates").insert([{
        name: templateName,
        blocks: blocks.map((b) => ({
          block_type: b.block_type,
          title: b.title,
          description: b.description,
          duration_minutes: b.duration_minutes,
        })) as unknown as Record<string, unknown>[],
        created_by: user!.id,
        organization_id: organization!.id,
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["session-templates"] });
      setShowTemplateSave(false);
      setTemplateName("");
      toast({ title: t("training.templateSaved") });
    },
  });

  const loadTemplate = (templateBlocks: Block[]) => {
    setBlocks(
      templateBlocks.map((b): Block => ({
        block_type: b.block_type || "other",
        title: b.title || "",
        description: b.description || "",
        duration_minutes: b.duration_minutes || null,
        exercise_id: null,
      }))
    );
    setShowTemplateLoad(false);
    toast({ title: t("training.templateLoaded") });
  };

  const addBlock = () => {
    setBlocks([...blocks, { block_type: "drill", title: "", description: "", duration_minutes: 15, exercise_id: null }]);
  };

  const updateBlock = (index: number, field: keyof Block, value: string | number | null) => {
    const updated = [...blocks];
    (updated[index] as Record<string, unknown>)[field] = value;
    // Auto-fill title from exercise
    if (field === "exercise_id" && value) {
      const ex = exercises.find((e) => e.id === value);
      if (ex && !updated[index].title) updated[index].title = ex.name;
    }
    setBlocks(updated);
  };

  const removeBlock = (index: number) => setBlocks(blocks.filter((_, i) => i !== index));

  const moveBlock = (index: number, direction: -1 | 1) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= blocks.length) return;
    const updated = [...blocks];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setBlocks(updated);
  };

  const toggleFocus = (area: string) => {
    setFocusAreas((prev) =>
      prev.includes(area) ? prev.filter((a) => a !== area) : [...prev, area]
    );
  };

  const totalMinutes = blocks.reduce((s, b) => s + (b.duration_minutes || 0), 0);

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack}>
        <ArrowLeft className="h-4 w-4 mr-1" /> {t("training.backToSessionList")}
      </Button>

      {session && (
        <Card>
          <CardContent className="py-3 px-4">
            <div className="flex items-center gap-3 flex-wrap text-sm">
              {(session.training_groups as Record<string, unknown>)?.color && (
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: (session.training_groups as Record<string, unknown>).color as string }} />
              )}
              <span className="font-medium">{(session.training_groups as Record<string, unknown>)?.name as string}</span>
              <span>{format(new Date(session.session_date), "EEE d MMM yyyy", { locale: sv })}</span>
              <span>{session.start_time?.slice(0, 5)} ({session.duration_minutes} min)</span>
              {(session.profiles as Record<string, unknown> | null)?.full_name && <span>{(session.profiles as Record<string, unknown>).full_name as string}</span>}
              {(session.courts as Record<string, unknown> | null)?.name && <span>{(session.courts as Record<string, unknown>).name as string}</span>}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Focus areas */}
      <div>
        <Label className="mb-1.5 block">{t("training.focusAreas")}</Label>
        <div className="flex flex-wrap gap-1.5">
          {FOCUS_AREAS.map((area) => (
            <Badge
              key={area.value}
              variant={focusAreas.includes(area.value) ? "default" : "outline"}
              className="cursor-pointer"
              onClick={() => toggleFocus(area.value)}
            >
              {t(area.labelKey)}
            </Badge>
          ))}
        </div>
      </div>

      {/* Summary */}
      <div>
        <Label className="mb-1.5 block">{t("training.summary")}</Label>
        <Textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={2} placeholder={t("training.summaryPlaceholder")} />
      </div>

      {/* Template actions */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => setShowTemplateLoad(!showTemplateLoad)}>
          <FileDown className="h-3.5 w-3.5 mr-1" /> {t("training.loadTemplate")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setShowTemplateSave(!showTemplateSave)} disabled={blocks.length === 0}>
          <FileUp className="h-3.5 w-3.5 mr-1" /> {t("training.saveAsTemplate")}
        </Button>
      </div>

      {showTemplateLoad && templates.length > 0 && (
        <Card>
          <CardContent className="py-3 px-4 space-y-1.5">
            {templates.map((tpl) => (
              <Button key={tpl.id} variant="ghost" size="sm" className="w-full justify-start" onClick={() => loadTemplate(tpl.blocks as unknown as Block[])}>
                {tpl.name} ({(tpl.blocks as unknown as Block[]).length} block)
              </Button>
            ))}
          </CardContent>
        </Card>
      )}

      {showTemplateSave && (
        <div className="flex gap-2">
          <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder={t("training.templateNamePlaceholder")} className="max-w-xs" />
          <Button size="sm" onClick={() => saveTemplateMutation.mutate()} disabled={!templateName.trim()}>
            {t("training.save")}
          </Button>
        </div>
      )}

      {/* Blocks */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>{t("training.blocks")} ({blocks.length}) — {totalMinutes} {t("training.minTotal")}</Label>
          <Button size="sm" variant="outline" onClick={addBlock}>
            <Plus className="h-3.5 w-3.5 mr-1" /> {t("training.addBlock")}
          </Button>
        </div>

        <div className="space-y-2">
          {blocks.map((block, i) => (
            <Card key={i}>
              <CardContent className="py-3 px-4">
                <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr_80px_160px_auto] gap-2 items-start">
                  <Select value={block.block_type} onValueChange={(v) => updateBlock(i, "block_type", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BLOCK_TYPES.map((bt) => (
                        <SelectItem key={bt.value} value={bt.value}>{t(bt.labelKey)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input value={block.title} onChange={(e) => updateBlock(i, "title", e.target.value)} placeholder={t("training.title")} />

                  <NumberInput
                    value={block.duration_minutes ?? undefined}
                    onValueChange={(v) => updateBlock(i, "duration_minutes", v || null)}
                    placeholder={t("training.minAbbrev")}
                    min={1}
                  />

                  <Select
                    value={block.exercise_id || "none"}
                    onValueChange={(v) => updateBlock(i, "exercise_id", v === "none" ? null : v)}
                  >
                    <SelectTrigger><SelectValue placeholder={t("training.exercisePlaceholder")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— {t("training.none")} —</SelectItem>
                      {exercises.map((ex) => (
                        <SelectItem key={ex.id} value={ex.id}>{ex.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <div className="flex gap-0.5">
                    <Button size="icon" variant="ghost" onClick={() => moveBlock(i, -1)} disabled={i === 0}>
                      <ChevronUp className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1}>
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => removeBlock(i)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={block.description}
                  onChange={(e) => updateBlock(i, "description", e.target.value)}
                  placeholder={t("training.descriptionPlaceholder")}
                  rows={1}
                  className="mt-2"
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
        <Save className="h-4 w-4 mr-1" /> {t("training.savePlan")}
      </Button>
    </div>
  );
}
